(() => {
  const hostSelector = '[data-yx-wishlist-host]';
  const toggleSelector = '[data-yx-wishlist-toggle]';
  const headerSelector = '[data-yx-wishlist-trigger]';
  const widgetSelector = '.wishlist-page-widget';
  const countSelector = '.wishlist-total-count';
  const observedHosts = new WeakSet();
  const isSpanish = document.documentElement.lang.toLowerCase().startsWith('es');

  function setText(element, text) {
    if (element && element.textContent.trim() !== text) element.textContent = text;
  }

  function localizeWishlistPopup(root = document) {
    if (!isSpanish) return;

    const popup = root instanceof Element && root.matches('#sewp-engine-popup')
      ? root
      : root.querySelector?.('#sewp-engine-popup') || document.querySelector('#sewp-engine-popup');
    if (!popup) return;

    setText(popup.querySelector('.wishlist-heading'), 'Mi lista de deseos');

    const login = [...popup.querySelectorAll('a')]
      .find((link) => ['login', 'iniciar sesión'].includes(link.textContent.trim().toLowerCase()))
      || popup.querySelector('a[href*="/account"]');
    setText(login, 'Iniciar sesión');
    if (login && window.YXWishlistAccountUrl) login.href = window.YXWishlistAccountUrl;

    const search = popup.querySelector('#wishlist-input');
    if (search instanceof HTMLInputElement) {
      if (search.placeholder !== 'Buscar producto') search.placeholder = 'Buscar producto';
      if (search.getAttribute('aria-label') !== 'Buscar producto') {
        search.setAttribute('aria-label', 'Buscar producto');
      }
    }

    popup.querySelectorAll('.wishlist-move-cart').forEach((button) => {
      if (button.textContent.trim().toLowerCase() === 'move to cart') setText(button, 'MOVER AL CARRITO');
    });
    popup.querySelectorAll('.wishlist_page_remove_product').forEach((button) => {
      if (button.getAttribute('aria-label') !== 'Eliminar de la lista de deseos') {
        button.setAttribute('aria-label', 'Eliminar de la lista de deseos');
      }
    });

    const close = popup.querySelector('.sewp-close');
    if (close && close.getAttribute('aria-label') !== 'Cerrar lista de deseos') {
      close.setAttribute('aria-label', 'Cerrar lista de deseos');
    }
  }

  function localizeWishlistNotice(root = document) {
    if (!isSpanish) return;

    const notices = [];
    if (root instanceof Element && root.matches('.wishlist-popup')) notices.push(root);
    root.querySelectorAll?.('.wishlist-popup').forEach((notice) => notices.push(notice));

    notices.forEach((notice) => {
      const heading = notice.querySelector('.wishlist-p-right h3');
      const close = notice.querySelector('.close-wishlist');
      if (close) close.setAttribute('aria-label', 'Cerrar notificación de la lista de deseos');
      if (!heading) return;

      const message = heading.textContent.trim().replace(/\s+/g, ' ');
      if (message.startsWith('Item has been temporarily added to wishlist')) {
        const login = document.createElement('a');
        login.href = window.YXWishlistAccountUrl || '/es/account/login';
        login.textContent = 'Inicia sesión';
        heading.replaceChildren(
          'El artículo se ha añadido temporalmente a la lista de deseos. ',
          login,
          ' para guardarlo permanentemente.',
        );
        return;
      }

      if (message.startsWith('Item has been successfully added to wishlist')) {
        const wishlist = document.createElement('a');
        wishlist.href = `${window.Shopify?.routes?.root || '/es/'}apps/wishlist`;
        wishlist.textContent = 'Mi lista de deseos';
        heading.replaceChildren(
          'El artículo se ha añadido correctamente a tu lista de deseos. Ver ',
          wishlist,
          '.',
        );
        return;
      }

      if (message.startsWith('Please login to add this item to your wishlist')) {
        const login = document.createElement('a');
        login.href = window.YXWishlistAccountUrl || '/es/account/login';
        login.textContent = 'Inicia sesión';
        heading.replaceChildren(
          login,
          ' para añadir este artículo a tu lista de deseos.',
        );
        return;
      }

      const translatedMessages = {
        'Item has been successfully removed from your wishlist': 'El artículo se ha eliminado correctamente de tu lista de deseos.',
        'Product details not found!': '¡No se encontraron los detalles del producto!',
        'Wishlist url copied successfully!': '¡El enlace de la lista de deseos se ha copiado correctamente!',
        'Item added to your cart.': 'El artículo se ha añadido al carrito.',
        'Wishlist not found!': '¡No se encontró la lista de deseos!',
        'Item added to your wishlist!': '¡El artículo se ha añadido a tu lista de deseos!',
      };
      if (translatedMessages[message]) setText(heading, translatedMessages[message]);
    });
  }

  function wishlistCount() {
    const badge = document.querySelector(`${widgetSelector} ${countSelector}`)
      || document.querySelector(countSelector);
    const rawCount = badge?.dataset.total || badge?.textContent || '0';
    const count = Number.parseInt(rawCount, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  function syncHeader() {
    const count = wishlistCount();
    document.querySelectorAll(headerSelector).forEach((header) => {
      const badge = header.querySelector('[data-yx-wishlist-count]');
      header.classList.toggle('is-wishlisted', count > 0);
      const label = isSpanish
        ? (count > 0 ? `Lista de deseos, ${count} artículos` : 'Lista de deseos')
        : (count > 0 ? `Wishlist, ${count} items` : 'Wishlist');
      header.setAttribute('aria-label', label);
      if (!badge) return;
      const displayCount = count > 99 ? '99+' : String(count);
      if (badge.textContent !== displayCount) badge.textContent = displayCount;
      badge.hidden = count === 0;
    });
  }

  function visibleCartCount() {
    const counts = [...document.querySelectorAll('.yx-site-header__cart-icon [data-testid="cart-bubble"]')];
    const visibleCount = counts.find((count) => !count.classList.contains('hidden')) || counts[0];
    const value = Number.parseInt(visibleCount?.textContent || '0', 10);
    return Number.isFinite(value) ? value : 0;
  }

  async function fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to refresh cart state.');
    return response.json();
  }

  async function syncCartAfterWishlistMove(button, previousCount) {
    const variantId = button.dataset.variant_id || '';
    let lastCount = previousCount;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      try {
        const cart = await fetchCart();
        const itemCount = Number(cart.item_count || 0);
        if (itemCount === lastCount) continue;
        lastCount = itemCount;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: {
              itemCount,
              source: 'yx-wishlist',
              variantId,
            },
          },
        }));
      } catch (_) {
        // Keep polling briefly; the app may still be completing its cart request.
      }
    }
  }

  function engineFor(host) {
    return host?.querySelector('.wishlist-engine') || null;
  }

  function syncHost(host) {
    const engine = engineFor(host);
    const scope = host?.closest('[data-yx-wishlist-scope]');
    if (!engine || !scope) return;

    const added = engine.dataset.added === 'true';
    scope.querySelectorAll(toggleSelector).forEach((toggle) => {
      toggle.classList.toggle('is-wishlisted', added);
      toggle.setAttribute('aria-pressed', String(added));
      const label = isSpanish
        ? (added ? 'Quitar de la lista de deseos' : 'Añadir a la lista de deseos')
        : (added ? 'Remove from wishlist' : 'Add to wishlist');
      if (engine.getAttribute('aria-label') !== label) engine.setAttribute('aria-label', label);
      toggle.setAttribute('aria-label', label);
    });
  }

  function observeHost(host) {
    if (!(host instanceof HTMLElement) || observedHosts.has(host)) return;
    observedHosts.add(host);
    const engine = engineFor(host);
    if (!engine) return;

    new MutationObserver(() => syncHost(host)).observe(engine, {
      attributes: true,
      attributeFilter: ['data-added', 'aria-label'],
      childList: true,
      subtree: true,
    });
    syncHost(host);
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches(hostSelector)) observeHost(root);
    root.querySelectorAll?.(hostSelector).forEach(observeHost);
    syncHeader();
    localizeWishlistPopup(root);
    localizeWishlistNotice(root);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const moveToCart = event.target.closest('.wishlist-move-cart');
    if (moveToCart instanceof HTMLButtonElement) {
      syncCartAfterWishlistMove(moveToCart, visibleCartCount());
      return;
    }

    const headerTrigger = event.target.closest(headerSelector);
    if (headerTrigger) {
      const widget = document.querySelector(widgetSelector);
      if (widget instanceof HTMLElement) {
        event.preventDefault();
        widget.click();
      }
      return;
    }

    const toggle = event.target.closest(toggleSelector);
    if (!toggle) return;

    const scope = toggle.closest('[data-yx-wishlist-scope]');
    const host = scope?.querySelector(hostSelector);
    const appButton = host?.querySelector('.wishlist-engine-button');
    if (!(appButton instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    appButton.click();
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const scope = event.target.closest('[data-yx-wishlist-scope]');
    const host = scope?.querySelector(hostSelector);
    const engine = engineFor(host);
    if (!host || !engine || !event.target.value) return;
    host.dataset.yxWishlistVariantId = event.target.value;
    engine.dataset.variant_id = event.target.value;
  });

  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
    syncHeader();
    localizeWishlistPopup();
    localizeWishlistNotice();
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-added', 'data-total'],
    childList: true,
    characterData: true,
    subtree: true,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();
